import { useEffect, useState } from 'react'
import { adminApiService } from '../../services/adminApiService'
import type { AdoptionFeature, PlatformAnalytics } from '../../services/analyticsApiService'
import { ApiError } from '../../services/BaseApiService'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import { capitalize, fmtNum, fmtPct, formatLabel, ratio, surfaceLabel } from '../../utils/analyticsFormat'
import {
  BarList, card, DailyChart, EmptyState, ErrorNote, gridAuto, KpiCard, numTd, RangePicker, SectionTitle, TableWrap,
  td, th, VIEWS_EMPTY_TEXT,
} from '../../components/analytics/AnalyticsUi'
import AdminTabs from './AdminTabs'

const FEATURE_LABELS: Record<AdoptionFeature, string> = {
  share_links: 'Share links',
  preview_links: 'Preview links',
  esign: 'E-signature',
  embed: 'Embeds',
  approvals: 'Promotion approvals',
  connectors: 'Connectors',
}

export default function AdminAnalyticsPage() {
  const [days, setDays] = useState(30)
  const [data, setData] = useState<PlatformAnalytics | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let live = true
    setLoading(true); setError('')
    adminApiService.getPlatformAnalytics(days)
      .then(d => { if (live) setData(d) })
      .catch(e => { if (live) setError(e instanceof ApiError ? e.message : 'Failed to load platform analytics') })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [days])

  const t = data?.totals

  return (
    <div>
      <AdminTabs />
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md, marginBottom: spacing.lg, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: colors.text, letterSpacing: '-0.02em', margin: 0, flex: 1 }}>
          Platform analytics
        </h1>
        <RangePicker days={days} onChange={setDays} />
      </div>
      {error && <ErrorNote message={error} />}
      {loading && !data && <div style={{ color: colors.textSecondary }}>Loading…</div>}
      {data && t && (
        <>
          <div style={{ ...gridAuto(140), marginBottom: spacing.lg }}>
            <KpiCard label="Teams" value={fmtNum(t.teams)} />
            <KpiCard label="Active teams" value={fmtNum(t.activeTeams)} hint={`${fmtPct(ratio(t.activeTeams, t.teams))} of teams`} />
            <KpiCard label="Documents" value={fmtNum(t.documents)} />
            <KpiCard label="Pages" value={fmtNum(t.pages)} />
            <KpiCard label="Failure rate" value={fmtPct(ratio(t.failed, t.documents))} hint={`${fmtNum(t.failed)} failed`}
              tone={t.documents > 0 && t.failed / t.documents > 0.05 ? colors.error : undefined} />
            <KpiCard label="Views" value={fmtNum(t.views)} />
            <KpiCard label="Unique viewers" value={fmtNum(t.uniqueViewers)} />
          </div>

          <div style={{ ...card, marginBottom: spacing.lg }}>
            <SectionTitle>Documents vs views</SectionTitle>
            {t.documents === 0 && t.views === 0 ? (
              <EmptyState title="No activity in this period">No team has rendered or viewed a document yet.</EmptyState>
            ) : (
              <DailyChart rows={data.daily} series={[
                { key: 'documents', label: 'Documents', color: colors.primary },
                { key: 'views', label: 'Views', color: colors.info },
                { key: 'activeTeams', label: 'Active teams', color: colors.warning },
              ]} />
            )}
          </div>

          <SectionTitle>Top teams</SectionTitle>
          <div style={{ marginBottom: spacing.lg }}>
            {data.topTeams.length === 0 ? (
              <div style={card}><EmptyState title="No team activity in this period" /></div>
            ) : (
              <TableWrap>
                <thead>
                  <tr>
                    <th style={th}>Team</th>
                    <th style={th}>Plan</th>
                    <th style={{ ...th, textAlign: 'right' }}>Documents</th>
                    <th style={{ ...th, textAlign: 'right' }}>Pages</th>
                    <th style={{ ...th, textAlign: 'right' }}>Views</th>
                    <th style={{ ...th, textAlign: 'right' }}>Failure %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topTeams.map(r => (
                    <tr key={r.teamId} data-testid="top-team">
                      <td style={{ ...td, fontWeight: 500 }}>{r.name}</td>
                      <td style={td}>
                        <span style={{
                          fontSize: fontSize.xs, padding: '2px 8px', borderRadius: radius.full,
                          border: `1px solid ${colors.border}`, color: colors.textSecondary,
                        }}>{capitalize(r.plan)}</span>
                      </td>
                      <td style={numTd}>{fmtNum(r.documents)}</td>
                      <td style={numTd}>{fmtNum(r.pages)}</td>
                      <td style={numTd}>{fmtNum(r.views)}</td>
                      <td style={{ ...numTd, color: r.failed ? colors.error : colors.textSecondary }}>{fmtPct(ratio(r.failed, r.documents))}</td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            )}
          </div>

          <div style={gridAuto(260)}>
            <div style={card}>
              <SectionTitle>By output format</SectionTitle>
              <BarList items={data.byFormat.map(f => ({ label: formatLabel(f.format), value: f.documents, sub: `${fmtNum(f.pages)} pages` }))}
                emptyText="No documents generated in this period." />
            </div>
            <div style={card}>
              <SectionTitle>Views by surface</SectionTitle>
              {t.views === 0 ? <EmptyState title="No views yet">{VIEWS_EMPTY_TEXT}</EmptyState> : (
                <BarList color={colors.info} items={data.bySurface.map(s => ({ label: surfaceLabel(s.surface), value: s.views, sub: `${fmtNum(s.uniqueViewers)} unique` }))} />
              )}
            </div>
            <div style={card} data-testid="feature-adoption">
              <SectionTitle>Feature adoption</SectionTitle>
              <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginBottom: spacing.sm }}>
                Teams that used each feature in this period, as a share of active teams.
              </div>
              <BarList
                color={colors.success}
                emptyText="No feature usage in this period."
                items={(Object.keys(FEATURE_LABELS) as AdoptionFeature[]).map(f => {
                  const teams = data.featureAdoption.find(a => a.feature === f)?.teams ?? 0
                  return { label: FEATURE_LABELS[f], value: teams, sub: `${fmtPct(ratio(teams, t.activeTeams))} of active` }
                })}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
